import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonUtilService } from 'src/app/shared/services/CommonUtil.service';
import { WebhooksApiService } from 'src/app/shared/services/api/webhooks-api.service';
import { OfflineExportsExportRequestDto, CustomApp, WebhookRequestBody } from 'src/app/shared/dto';
import { REQUEST_TYPE, OWNER_TYPE } from 'src/app/shared/enums';
import { SiModalModule } from '../si-modal/si-modal.module';

export class FailedContactModel {
  matchId: string;
  contactName: string;
  companyName: string;
  errorMessage: string;
}

@UntilDestroy()
@Component({
  selector: 'app-account-export-app-wizard',
  templateUrl: './account-export-app-wizard.component.html',
  styleUrls: ['./account-export-app-wizard.component.scss'],
  imports: [CommonModule, FormsModule, SiModalModule, MatProgressSpinner, MatButton],
  standalone: true
})
export class AccountExportAppWizardComponent implements OnInit {
  pages = [
    {
      name: 'loading',
      skip: false,
    },
    {
      name: 'accountOwner',
      skip: true,
    },
    {
      name: 'contactType',
      skip: true,
    },
    {
      name: 'contactOwner',
      skip: true,
    },
    {
      name: 'campaignsWorkflow',
      skip: true,
    },
    {
      name: 'export',
      skip: false,
    },
    {
      name: 'exportFailed',
      skip: true,
    },
    {
      name: 'exportEmpty',
      skip: true,
    },
    {
      name: 'exportFinished',
      skip: false,
    },
  ];

  contacts = [];
  companies = [];
  itemsCount: number = 0;
  currentPage = this.pages[0];
  initialPage = this.pages[0];
  typeOfRecord: REQUEST_TYPE;
  app: CustomApp;
  isBusy: boolean = false;
  isExportButton: boolean = false;
  isExportWizardOpened: boolean = false;
  showPreviousButton = false;
  showNextButton = false;
  showCloseButton: boolean = false;
  showCancelButton: boolean = true;
  showRetryButton: boolean = false;
  humanverified = [];
  machineverified = [];
  exportedContactCount = 0;
  busyLabel = 'Loading ...';
  busySublabel = '';
  contactType: string = null;
  accountOwner: string = null;
  campaignWorkflow: string = null;
  contactOwner: string = null;
  toBeExported = [];
  requestBody: OfflineExportsExportRequestDto;
  requestType: typeof REQUEST_TYPE = REQUEST_TYPE;
  icpIntelJobUUID: string;

  constructor(
    private commonUtilService: CommonUtilService,
    private readonly webhooksApiService: WebhooksApiService,
    private readonly matDialogRef: MatDialogRef<AccountExportAppWizardComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
  ) {}

  ngOnInit(): void {

    this.icpIntelJobUUID = this.data['icpIntelJobUUID'] || null;

    if (this.data['contacts']) {
      this.contacts = this.data['contacts'];
    }

    if (this.data['company']) {
      this.companies = this.data['company'];
    }

    this.itemsCount = this.data?.itemsCount || 0;

    if (this.data['requestBody']) {
      this.requestBody = this.data['requestBody'];
    }

    if (this.data['app']) {
      this.app = this.data['app'];
      this.typeOfRecord = this.app.request_type;
    }

    this.initializeFirstStep();
  }

  initializeFirstStep() {
    this.initialPage = this.currentPage;

    if (this.typeOfRecord === REQUEST_TYPE.COMPANIES) {
      if (this.app?.include_account_owner && this.app?.account_owner_contact_type === OWNER_TYPE.LIST) {
        this.pages.find((choice) => choice.name === 'accountOwner').skip = false;
      }
    } else {
      if (this.app?.include_contact_type) {
        this.pages.find((choice) => choice.name === 'contactType').skip = false;
      }
      if (this.app?.include_contact_owner && this.app?.contact_owner_contact_type === OWNER_TYPE.LIST) {
        this.pages.find((choice) => choice.name === 'contactOwner').skip = false;
      }
      if (this.app?.is_add_to_campaign_workflow) {
        this.pages.find((choice) => choice.name === 'campaignsWorkflow').skip = false;
      }
    }

    this.next();
  }

  next() {
    this.currentPage = this.pages[this.pages.indexOf(this.currentPage) + 1];

    this.currentPage.skip ? this.next() : null;
    this.executeCurrentStep();
  }

  previous() {
    this.isExportButton = false;
    this.currentPage = this.pages[this.pages.indexOf(this.currentPage) - 1];
    this.currentPage.skip ? this.previous() : null;
    this.executeCurrentStep();
  }

  onClose() {
    this.isExportWizardOpened = false;
    this.matDialogRef.close();
  }

  executeCurrentStep() {
    switch (this.currentPage.name) {
      case 'contactType':
        this.showCancelButton = true;
        this.showPreviousButton = false;

        if (this.app?.is_contact_type_required || !!this.contactType) {
          this.showNextButton = true;
        }
        break;

      case 'contactOwner':
        this.showCancelButton = true;
        this.showNextButton = this.app?.is_contact_owner_required || !!this.contactOwner;

        if (this.app?.include_contact_type) {
          this.showPreviousButton = true;
        }
        break;

      case 'campaignsWorkflow':
        this.showCancelButton = true;
        this.showNextButton = this.app?.is_campaign_workflow_required || !!this.campaignWorkflow;

        if (this.app?.include_contact_type || this.app?.include_contact_owner) {
          this.showPreviousButton = true;
        }
        break;

      case 'accountOwner':
        this.showCancelButton = true;
        this.showPreviousButton = false;

        if (this.app?.is_account_owner_required || !!this.accountOwner) {
          this.showNextButton = true;
        }
        break;
      case 'export':
        this.showPreviousButton = this.app?.include_account_owner || this.app?.include_campaign_workflow ||
            this.app?.include_contact_owner || this.app?.include_contact_type;

        this.showCancelButton = true;
        this.showNextButton = false;
        this.isExportButton = true;
        break;
      case 'exportFinished':
        this.showCancelButton = false;
        this.showNextButton = false;
        this.isExportButton = false;
        this.showCloseButton = true;
        this.showPreviousButton = false;
        break;
      case 'exportFailed':
        this.showCancelButton = false;
        this.showNextButton = false;
        this.isExportButton = false;
        this.showCloseButton = true;
        this.showRetryButton = true;
        this.showPreviousButton = false;
        break;
      default:
        break;
    }
  }

  setContactType(contactType) {
    if (this.contactType === contactType) {
      this.contactType = null;

      this.showNextButton = this.app?.is_contact_type_required;
    } else {
      this.contactType = contactType;
      this.showNextButton = true;
    }
  }

  setContactOwner(contactOwner) {
    if (this.contactOwner === contactOwner) {
      this.contactOwner = null;

      this.showNextButton = this.app?.is_contact_owner_required;
    } else {
      this.contactOwner = contactOwner;
      this.showNextButton = true;
    }
  }

  setCampaignsWorkflow(campaignWorkflow) {
    if (this.campaignWorkflow === campaignWorkflow) {
      this.campaignWorkflow = null;
      this.showNextButton = this.app?.is_campaign_workflow_required;
    } else {
      this.campaignWorkflow = campaignWorkflow;
      this.showNextButton = true;
    }
  }

  setAccountOwner(accountOwner) {
    if (this.accountOwner === accountOwner) {
      this.accountOwner = null;
      this.showNextButton = this.app?.is_account_owner_required;
    } else {
      this.accountOwner = accountOwner;
      this.showNextButton = true;
    }

  }

  export() {
    const webhookRequestBody: WebhookRequestBody = {
      contact_type: this.contactType,
      contact_owner: this.contactOwner,
      account_owner: this.accountOwner,
      campaign_workflow: this.campaignWorkflow,
    };

    this.webhooksApiService
      .export(this.app, webhookRequestBody)
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {

      let ids = [];

      if (this.typeOfRecord === REQUEST_TYPE.COMPANIES) {
        ids = this.companies?.map(id => id.companyId ? id.companyId : id);
      } else {
        ids = this.contacts?.map(id => id.matchId ? id.matchId : id);
      }

      const jobContactsId = res? res : null;
      const properties = {
        ids,
        requestBody: this.requestBody,
        request_type: this.typeOfRecord,
        webhookId: this.app?.id,
        jobContactsId,
        webhookRequestBody,
      }

      this.commonUtilService.exportCsvWebhooks(properties).pipe(
          untilDestroyed(this),
          catchError(() => {
            this.pages.find((choice) => choice.name === 'exportFailed').skip = false;
            return of();
          })
        )
        .subscribe((res: any) => {
          this.pages.find((choice) => choice.name === 'exportFinished').skip = false;
        });

      this.next()
    });

    this.busyLabel = '';
    this.busySublabel =
      'We have begun processing your export request.  This may take 30 minutes or more during busy times.';
    this.isBusy = true;
    this.showNextButton = false;
    this.showPreviousButton = false;
    this.isBusy = false;
  }

  formatDateTime() {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    let hours = currentDate.getHours();
    const minutes = currentDate.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    if (hours === 0) {
        hours = 12;
    }
    const hourStr = hours < 10 ? '0' + hours : hours;
    const minuteStr = minutes < 10 ? '0' + minutes : minutes;
    const dateTime = month.toString() + day.toString() + year.toString() + '_';
    const strDateTime = dateTime + hourStr + minuteStr + ampm.toString();
    return strDateTime;
}
}
